import { revalidateLogic } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useAppForm } from "@/components/form/tanstack-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import { useSession } from "@/providers/session-provider";
import { Collections, type Create, SupportTicketsStatusOptions } from "@/types/pocketbase.ts";

const formSchema = z.object({
	email: z.email(),
	subject: z.string().min(1),
	message: z.string().min(10),
	status: z.enum(SupportTicketsStatusOptions),
});

type FormValues = z.infer<typeof formSchema>;

type SupportFormProps = {
	onSuccess?: () => void;
};

export const SupportForm = ({ onSuccess }: SupportFormProps) => {
	const session = useSession();
	const { t } = useTranslation();

	const defaultValues = {
		email: session?.email ?? "",
		subject: "",
		message: "",
		status: SupportTicketsStatusOptions.open,
	};

	const createTicket = useMutation({
		mutationFn: (payload: FormValues) => {
			toast.loading(
				t(($) => $.modules.support.form.status.loading.title),
				{
					id: "support-ticket",
					description: t(($) => $.modules.support.form.status.loading.message),
				}
			);

			const values: Create<Collections.SupportTickets> = {
				...payload,
				email: payload.email.toLowerCase(),
			};

			return pb.collection(Collections.SupportTickets).create(values);
		},
		onSuccess: () => {
			toast.success(
				t(($) => $.modules.support.form.status.success.title),
				{
					id: "support-ticket",
					description: t(($) => $.modules.support.form.status.success.message),
				}
			);
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, defaultValues);

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.support.form.status.error.title),
					{
						id: "support-ticket",
						description: parsed.message || t(($) => $.modules.support.form.status.error.message),
					}
				);
			}

			form.setErrorMap({ onDynamic: parsed.errorMap });
			toast.dismiss("support-ticket");
		},
	});

	const form = useAppForm({
		defaultValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: formSchema },

		onSubmit: ({ value }) => {
			createTicket.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form]
	);

	return (
		<form.AppForm>
			<form className="space-y-6" onSubmit={handleSubmit}>
				<form.AppField name="email">
					{(field) => (
						<field.Field>
							<field.FieldLabel>{t(($) => $.modules.support.form.fields.email.label)}</field.FieldLabel>
							<field.FieldControl>
								<Input
									autoComplete="email"
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder={t(($) => $.modules.support.form.fields.email.placeholder)}
									type="email"
									value={field.state.value}
								/>
							</field.FieldControl>
							<field.FieldError />
						</field.Field>
					)}
				</form.AppField>

				<form.AppField name="subject">
					{(field) => (
						<field.Field>
							<field.FieldLabel>{t(($) => $.modules.support.form.fields.subject.label)}</field.FieldLabel>
							<field.FieldControl>
								<Input
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder={t(($) => $.modules.support.form.fields.subject.placeholder)}
									value={field.state.value}
								/>
							</field.FieldControl>
							<field.FieldError />
						</field.Field>
					)}
				</form.AppField>

				<form.AppField name="message">
					{(field) => (
						<field.Field>
							<field.FieldLabel>{t(($) => $.modules.support.form.fields.message.label)}</field.FieldLabel>
							<field.FieldControl>
								<Textarea
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder={t(($) => $.modules.support.form.fields.message.placeholder)}
									value={field.state.value}
								/>
							</field.FieldControl>
							<field.FieldError />
						</field.Field>
					)}
				</form.AppField>

				<Button className="w-full" disabled={createTicket.isPending} type="submit">
					{t(($) => $.common.actions.save)}
				</Button>
			</form>
		</form.AppForm>
	);
};
